// SEQ-182 T10: data hook behind the P&L tab's operational hours band
// (formerly PnlOperationalRows.tsx, in ProjectStatusTab.tsx's Wages
// section; see the note below). Same shape as `usePnlAnalyticsProjectTrend` in
// usePnlAnalytics.ts:174-188 (one project, groupBy: "month"), built on the
// same exported `useOnePnlAnalyticsGrain`, so there is no new server
// surface: this is the existing `pnl_analytics_query` RPC, requesting only
// the three hours keys the band renders rather than the analytics page's
// full rollup set.
//
// Never touches `claims_pnl_monthly_values`, `usePnlCategories`, or
// `app_settings.pnl_expense_categories` (plan section 6.1): the band is
// read-only and the RPC is the only data path in or out of this module.
//
// NOT MOUNTED SINCE 17 SEPTEMBER 2026. Labour hours became manual input
// (20260917000100_pnl_manual_labour_hours.sql), so PnlOperationalRows.tsx
// was removed and its three rows are rendered by PnlManualMetricRows.tsx
// from pnl_manual_metric_value instead. The RPC still serves these keys, and
// this hook still works, but nothing renders it. `OperationalHoursMonthColumn`
// below is the live export: the manual band and its hook both take it.
// Deleting the rest is a tidy-up worth doing on its own, not inside the
// change that made hours enterable.

import { useMemo } from "react";
import { PNL_METRIC_KEYS } from "../../lib/pnl/pnlAnalytics";
import { useOnePnlAnalyticsGrain } from "./usePnlAnalytics";
import type { PnlAnalyticsQueryFilters } from "./usePnlAnalytics";
import { buildFixtureProjectTrendResponse } from "./fixtures";
import { totalOperationalHours, isOperationalHoursEmpty } from "./operationalHoursTotals";
import type { OperationalHoursMonth, OperationalHoursTotals } from "./operationalHoursTotals";

export type { OperationalHoursMonth, OperationalHoursTotals };

/** The exact three metrics the band renders. Fetching only these, rather than the page's seven-metric PNL_ROLLUP_METRICS, keeps this call small. */
export const OPERATIONAL_HOURS_METRICS: string[] = [
  PNL_METRIC_KEYS.labour.ordinaryHours,
  PNL_METRIC_KEYS.labour.overtimeHours,
  PNL_METRIC_KEYS.calculated.totalLabourHours,
];

/** One tab-visible month column, the shape `getMonthRange` (pnlCalculationCore.ts) already returns. */
export interface OperationalHoursMonthColumn {
  month: number;
  year: number;
  key: string;
}

export interface UseProjectOperationalHoursResult {
  /** Null while loading or on error; otherwise one entry per fetched calendar month (may be fewer than monthColumns if the RPC has no row for a month). */
  months: OperationalHoursMonth[] | null;
  /** Client-side sum of `months`, recomputing total labour hours from the summed components (operationalHoursTotals.ts). Null while `months` is null. */
  totals: OperationalHoursTotals | null;
  loading: boolean;
  /** T11: RPC error message, or null. The P&L table itself must keep rendering when this is set; only the band shows the failure state. */
  error: string | null;
  /** T11: no month carried any of the three metrics (unmapped site, or a range entirely outside payroll data). Distinct from `error`. */
  isEmpty: boolean;
  isFixtureData: boolean;
}

function lastDayOfMonthIso(year: number, month: number): string {
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
}

/** 'YYYY-MM-01' periodMonth -> the tab's own 'YYYY-MM' month key, so the band's cells line up with the table's existing month columns. */
export function periodMonthToColumnKey(periodMonth: string): string {
  return periodMonth.slice(0, 7);
}

export function useProjectOperationalHours(
  projectId: string,
  monthColumns: OperationalHoursMonthColumn[],
): UseProjectOperationalHoursResult {
  const filters = useMemo<PnlAnalyticsQueryFilters>(() => {
    if (monthColumns.length === 0) {
      return { from: null, to: null, projectIds: [projectId], statuses: [] };
    }
    const first = monthColumns[0];
    const last = monthColumns[monthColumns.length - 1];
    return {
      from: `${first.year}-${String(first.month).padStart(2, "0")}-01`,
      to: lastDayOfMonthIso(last.year, last.month),
      projectIds: [projectId],
      statuses: [],
    };
  }, [projectId, monthColumns]);

  const fixtureResponse = useMemo(() => buildFixtureProjectTrendResponse(projectId), [projectId]);

  const { data, loading, error, isFixtureData } = useOnePnlAnalyticsGrain(
    filters,
    "month",
    OPERATIONAL_HOURS_METRICS,
    fixtureResponse,
    true,
  );

  const months = useMemo<OperationalHoursMonth[] | null>(() => {
    if (!data) return null;
    return data.rows
      .filter((row): row is typeof row & { periodMonth: string } => row.periodMonth !== null)
      .map((row) => ({
        periodMonth: row.periodMonth,
        ordinaryHours: row.metrics[PNL_METRIC_KEYS.labour.ordinaryHours] ?? null,
        overtimeHours: row.metrics[PNL_METRIC_KEYS.labour.overtimeHours] ?? null,
        totalLabourHours: row.metrics[PNL_METRIC_KEYS.calculated.totalLabourHours] ?? null,
      }))
      .sort((a, b) => a.periodMonth.localeCompare(b.periodMonth));
  }, [data]);

  const totals = useMemo<OperationalHoursTotals | null>(
    () => (months ? totalOperationalHours(months) : null),
    [months],
  );

  const isEmpty = months !== null && isOperationalHoursEmpty(months);

  return { months, totals, loading, error, isEmpty, isFixtureData };
}
